import React from 'react';

export default function ScorecardView({ scorecard, round, roundLabel, competitionPoints, hideHeader, hidePoints }) {

  if (!scorecard || !scorecard.scores) {
    return <div className="text-center py-8 text-gray-600">No scorecard available</div>;
  }

  const frontNine = scorecard.scores.slice(0, 9);
  const backNine = scorecard.scores.slice(9, 18);

  const calculateNineTotals = (holes) => {
    const hasNR = holes.some(h => h.strokes === 0);
    const totals = holes.reduce(
      (acc, hole) => ({
        par: acc.par + hole.par,
        strokes: acc.strokes + (hole.strokes || 0),
        points: acc.points + (hole.points || 0)
      }),
      { par: 0, strokes: 0, points: 0 }
    );
    return { ...totals, hasNR };
  };

  const frontTotals = calculateNineTotals(frontNine);
  const backTotals = calculateNineTotals(backNine);

  const getScoreBadge = (strokes, par) => {
    if (!strokes || strokes === 0) return null;
    const diff = strokes - par;
    if (diff <= -3) return 'bg-green-600 text-white rounded-full';
    if (diff === -2) return 'bg-yellow-400 text-gray-900 rounded-full';
    if (diff === -1) return 'bg-red-600 text-white rounded-full';
    if (diff === 0)  return null;
    if (diff === 1)  return 'border-2 border-gray-500 rounded text-gray-900';
    if (diff === 2)  return 'bg-blue-200 text-blue-900 rounded';
    return 'bg-blue-700 text-white rounded';
  };

  const isInProgress = round && (round.status === 'in_progress' || round.status === 'active');
  const isCompleted = round && round.status === 'completed';

  const renderNineHoles = (holes, isFront) => {
    const totals = isFront ? frontTotals : backTotals;
    const label = isFront ? 'Out' : 'In';

    return (
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          {/* label col ~13%, 9 hole cols ~7.7% each, total col ~7% */}
          <col style={{ width: '13%' }} />
          {holes.map((_, i) => <col key={i} style={{ width: '8%' }} />)}
          <col style={{ width: '7%' }} />
        </colgroup>
        <thead>
          <tr className="bg-gray-200 text-gray-600">
            <th className="border border-gray-100 px-2 py-1.5 text-left font-semibold text-xs">Hole</th>
            {holes.map((hole) => (
              <th key={hole.hole_number} className="border border-gray-100 py-1.5 text-center font-semibold text-xs">
                {hole.hole_number}
              </th>
            ))}
            <th className="border border-gray-100 py-1.5 text-center font-semibold text-xs text-gray-400">{label}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="text-gray-400">
            <td className="border border-gray-100 px-2 py-1 text-xs">SI</td>
            {holes.map((hole) => (
              <td key={hole.hole_number} className="border border-gray-100 py-1 text-center text-xs">
                {hole.stroke_index}
              </td>
            ))}
            <td className="border border-gray-100 py-1 bg-gray-50"></td>
          </tr>

          <tr className="text-gray-600">
            <td className="border border-gray-100 px-2 py-1 font-medium text-xs">Par</td>
            {holes.map((hole) => (
              <td key={hole.hole_number} className="border border-gray-100 py-1 text-center text-xs">
                {hole.par}
              </td>
            ))}
            <td className="border border-gray-100 py-1 text-center font-semibold text-xs text-gray-500">
              {totals.par}
            </td>
          </tr>

          <tr>
            <td className="border border-gray-100 px-2 py-1.5 font-semibold text-gray-800 text-xs whitespace-nowrap">Score</td>
            {holes.map((hole) => {
              const badgeClass = getScoreBadge(hole.strokes, hole.par);
              return (
                <td key={hole.hole_number} className="border border-gray-100 py-1 text-center">
                  {hole.strokes === 0 ? (
                    <span className="font-semibold text-gray-400 text-xs">NR</span>
                  ) : hole.strokes ? (
                    badgeClass ? (
                      <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold ${badgeClass}`}>
                        {hole.strokes}
                      </span>
                    ) : (
                      <span className="font-semibold text-gray-900 text-xs">{hole.strokes}</span>
                    )
                  ) : (
                    <span className="text-gray-300 text-xs">·</span>
                  )}
                </td>
              );
            })}
            <td className="border border-gray-100 py-1 text-center font-bold text-gray-800 text-xs">
              {totals.hasNR ? '-' : totals.strokes || '-'}
            </td>
          </tr>

          {!hidePoints && (
            <tr className="bg-gray-50">
              <td className="border border-gray-100 px-2 py-1.5 font-semibold text-gray-800 text-xs">Pts</td>
              {holes.map((hole) => (
                <td key={hole.hole_number} className="border border-gray-100 py-1.5 text-center font-bold text-gray-900 text-xs">
                  {hole.points !== null && hole.points !== undefined ? hole.points : ''}
                </td>
              ))}
              <td className="border border-gray-100 py-1.5 text-center font-bold text-gray-900 text-xs">
                {totals.points || ''}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div className="space-y-3">
      {!hideHeader && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mx-4">
          {(roundLabel || round) && (
            <p className="text-xs uppercase tracking-wide mb-1">
              {roundLabel && <span className="font-bold text-gray-900">{roundLabel}</span>}
              {roundLabel && (isInProgress || isCompleted) && <span className="text-gray-400 mx-1">·</span>}
              {isInProgress && <span className="font-bold text-green-600">In Progress</span>}
              {isCompleted && <span className="font-bold text-blue-600">Completed</span>}
            </p>
          )}
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-900">{scorecard.player_name}</h3>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium">
                {competitionPoints !== undefined ? 'Competition Points' : 'Total Points'}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {competitionPoints !== undefined ? competitionPoints : (scorecard.total_points || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Both nines — no horizontal scroll */}
      <div className="px-2 space-y-2">
        {renderNineHoles(frontNine, true)}
        {renderNineHoles(backNine, false)}
      </div>

      {/* Totals */}
      <div className="mx-2 mb-2 bg-white rounded-xl overflow-hidden">
        <div className={`grid divide-x divide-gray-100 ${hidePoints ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <div className="py-4 text-center">
            <p className="text-xs text-gray-400 font-medium mb-1">Par</p>
            <p className="text-xl font-bold text-gray-900">{frontTotals.par + backTotals.par}</p>
          </div>
          <div className="py-4 text-center">
            <p className="text-xs text-gray-400 font-medium mb-1">Strokes</p>
            <p className="text-xl font-bold text-gray-900">
              {(frontTotals.hasNR || backTotals.hasNR) ? '-' : scorecard.total_strokes || '-'}
            </p>
          </div>
          {!hidePoints && (
            <div className="py-4 text-center">
              <p className="text-xs text-gray-400 font-medium mb-1">Points</p>
              <p className="text-xl font-bold text-gray-900">{scorecard.total_points || 0}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
